import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR, { type KeyedMutator } from "swr";
import type {
  GetRegistryChangeAlertsAPIRequest,
  GetRegistryChangeAlertsAPIResponse,
} from "@/features/registryChangeAlert/types";
import { SendToTrackingService } from "@/api/handlers/errors";

export const REGISTRY_CHANGE_ALERTS_ENDPOINT = "/registry-change-alerts/";

interface IResponse {
  data: GetRegistryChangeAlertsAPIResponse | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<GetRegistryChangeAlertsAPIResponse>;
  error: AxiosError | undefined;
}

/**
 * 変動アラートの一覧を取得する（サーバサイドページング）。
 * @param {AxiosInstance} apiClient JWT 認証付き apiClient
 * @param {GetRegistryChangeAlertsAPIRequest} request 絞り込み・ページ
 * @returns {IResponse}
 */
export const GetRegistryChangeAlertsAPI = (
  apiClient: AxiosInstance,
  request: GetRegistryChangeAlertsAPIRequest
): IResponse => {
  const fetcher = async (
    url: string,
    params: GetRegistryChangeAlertsAPIRequest
  ): Promise<GetRegistryChangeAlertsAPIResponse> => {
    const { data } = await apiClient.get<GetRegistryChangeAlertsAPIResponse>(
      url,
      { params }
    );
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    GetRegistryChangeAlertsAPIResponse,
    AxiosError
  >(
    [REGISTRY_CHANGE_ALERTS_ENDPOINT, request],
    async ([url, params]: [string, GetRegistryChangeAlertsAPIRequest]) =>
      await fetcher(url, params),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
  }

  return { data, isLoading, mutate, error };
};
